import { useNavigate } from 'react-router-dom';
import { Building2, Package, ShoppingCart, ChefHat, DollarSign, Users, Shield, ArrowRight, CheckCircle2 } from 'lucide-react';

const features = [
  {
    icon: Package,
    title: 'Inventory Management',
    description: 'Real-time stock tracking, batch & expiry management',
    color: 'from-emerald-500 to-emerald-600',
  },
  {
    icon: ShoppingCart,
    title: 'Sales & POS',
    description: 'Multi-department checkout, loyalty programs, shift management',
    color: 'from-blue-500 to-blue-600',
  },
  {
    icon: ChefHat,
    title: 'Production & Kitchen',
    description: 'Recipes, bake logs, custom cake orders, quality control',
    color: 'from-amber-500 to-amber-600',
  },
  {
    icon: DollarSign,
    title: 'Finance & Accounting',
    description: 'P&L, cash flow, budgets, Nigerian tax compliance',
    color: 'from-purple-500 to-purple-600',
  },
  {
    icon: Users,
    title: 'Human Resources',
    description: 'Employee lifecycle, payroll, attendance, leave management',
    color: 'from-cyan-500 to-cyan-600',
  },
  {
    icon: Building2,
    title: 'Multi-Branch',
    description: 'Cross-branch operations, stock transfers, centralized control',
    color: 'from-rose-500 to-rose-600',
  },
];

const stats = [
  { label: 'Complete ERP Solution', icon: CheckCircle2 },
  { label: 'Multi-Branch Ready', icon: Building2 },
  { label: 'Nigerian Market Tailored', icon: Shield },
  { label: 'Enterprise Grade Security', icon: Shield },
];

export default function LandingPage() {
  const navigate = useNavigate();

  const scrollToFeatures = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#0F172A]">
      <nav className="relative z-20 flex items-center justify-between px-6 md:px-12 lg:px-20 py-5">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Office Suite" className="w-11 h-11 rounded-xl object-contain" />
          <div>
            <h2 className="text-white font-bold text-lg tracking-tight">Office Suite</h2>
            <p className="text-slate-400 text-[10px] leading-none">Enterprise Resource Planning</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/login')}
          className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:from-emerald-700 hover:to-emerald-800 transition-all duration-200 shadow-lg shadow-emerald-600/25"
        >
          Sign In
        </button>
      </nav>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 px-6 md:px-12 lg:px-20 pt-16 pb-24 md:pt-24 md:pb-32 max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full px-4 py-2 mb-8">
            <Shield className="w-4 h-4 text-emerald-400" />
            <span className="text-slate-300 text-sm">Enterprise-grade business management</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-6">
            Office Suite
            <span className="block text-emerald-400 mt-2">Enterprise Management System</span>
          </h1>

          <p className="text-slate-300 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-10">
            Complete business management for Supermarket, Bakery & Restaurant operations
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/login')}
              className="w-full sm:w-auto bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-8 py-4 rounded-xl font-semibold hover:from-emerald-700 hover:to-emerald-800 transition-all duration-200 shadow-lg shadow-emerald-600/25 flex items-center justify-center gap-2 text-base"
            >
              Sign In
              <ArrowRight className="w-5 h-5" />
            </button>
            <button
              onClick={scrollToFeatures}
              className="w-full sm:w-auto bg-white/5 backdrop-blur-sm border border-white/10 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/10 transition-all duration-200 text-base"
            >
              Learn More
            </button>
          </div>
        </div>
      </section>

      <section id="features" className="relative bg-gradient-to-b from-[#0F172A] to-[#1E293B] py-20 md:py-28">
        <div className="px-6 md:px-12 lg:px-20 max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Everything You Need to Run Your Business
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Six integrated modules designed specifically for supermarket, bakery, and restaurant operations.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                >
                  <div className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform duration-200`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">{feature.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative bg-[#1E293B] py-20 md:py-24">
        <div className="px-6 md:px-12 lg:px-20 max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className="text-center bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6"
                >
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <p className="text-white font-semibold text-base">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <footer className="bg-[#0F172A] border-t border-white/10 py-8">
        <div className="px-6 md:px-12 lg:px-20 max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">© 2026 Office Suite. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate('/login')}
              className="text-slate-400 hover:text-emerald-400 text-sm transition-colors"
            >
              Sign In
            </button>
            <span className="text-slate-700">|</span>
            <button
              onClick={() => window.location.href = 'mailto:support@office-suite.com'}
              className="text-slate-400 hover:text-emerald-400 text-sm transition-colors"
            >
              Contact Support
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
